import React from "react";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.css";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Directions from "../images/directions.jpg";
import styled, { keyframes } from "styled-components";
import Video from "../components/video";

// ── Animations ──────────────────────────────────────────
const heroReveal = keyframes`
  from { opacity: 0; transform: scale(1.1); }
  to   { opacity: 1; transform: scale(1);   }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0%   50%; }
  50%       { background-position: 100% 50%; }
`;

// ── Page Wrapper ─────────────────────────────────────────
const PageWrapper = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 25%, #dee2e6 50%, #f8f9fa 100%);
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  min-height: 100vh;
  position: relative;
  * { box-sizing: border-box; }
  .container, .container-fluid {
    max-width: none !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  overflow: hidden;
  animation: ${heroReveal} 2s ease-out;
  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 100px;
    background: linear-gradient(transparent, rgba(248,249,250,0.8));
    pointer-events: none;
  }
`;

const MaroonHeader = styled.h1`
  color: white;
  background: linear-gradient(135deg, #800000 0%, #a0522d 100%);
  text-align: center;
  padding: 30px 0;
  margin: 0;
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 700;
  letter-spacing: 2px;
  text-shadow: 0 4px 8px rgba(0,0,0,0.5);
  box-shadow: 0 8px 25px rgba(128,0,0,0.3);
`;

// ── Intro Section ────────────────────────────────────────
const IntroSection = styled.div`
  background: #fff;
  padding: 80px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid rgba(139,69,19,0.1);
`;

const IntroInner = styled.div`
  max-width: 820px;
  width: 100%;
  text-align: center;
  animation: ${fadeUp} 1s ease both;
`;

const IntroAccent = styled.div`
  width: 50px;
  height: 4px;
  background: linear-gradient(135deg, #FF8C00, #8B4513);
  border-radius: 2px;
  margin: 0 auto 28px;
`;

const IntroText = styled.p`
  color: #444;
  font-size: clamp(1rem, 1.7vw, 1.18rem);
  line-height: 1.95;
  margin: 0 0 20px 0;
  &:last-of-type { margin-bottom: 36px; }
`;

const IntroBookBtn = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #800000, #a0522d);
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  padding: 16px 50px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(128,0,0,0.25);
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px rgba(128,0,0,0.3);
    color: #fff;
    text-decoration: none;
  }
`;

const ContentContainer = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 30px;
`;

const WideContainer = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 30px;
`;

// ── Amenities Section ────────────────────────────────────
const AmenitiesSection = styled.section`
  background: #fff;
  padding: 90px 0 60px;
`;

const AmenitiesInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
`;

const HeroBanner = styled.div`
  position: relative;
  width: 100%;
  height: 520px;
  overflow: hidden;
  border-radius: 24px;
  margin-bottom: 90px;
  box-shadow: 0 30px 60px rgba(0,0,0,0.18);
  animation: ${fadeUp} 1s ease both;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform: scale(1.03);
    transition: transform 8s ease;
    &:hover { transform: scale(1.08); }
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(128,0,0,0.72) 0%,
    rgba(180,80,0,0.45) 55%,
    transparent 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 70px;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 18px;
  text-shadow: 0 3px 10px rgba(0,0,0,0.4);
  max-width: 580px;
`;

const HeroSub = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: clamp(1rem, 1.6vw, 1.2rem);
  max-width: 500px;
  line-height: 1.7;
  margin: 0;
`;

// ── Content Cards (no images) ────────────────────────────
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 90px;
  animation: ${fadeUp} 0.9s ease both;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContentCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 44px 36px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.07);
  border: 1px solid rgba(139,69,19,0.08);
  border-top: 4px solid transparent;
  border-image: linear-gradient(135deg, #FF8C00, #8B4513) 1;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FF8C00, #8B4513);
    border-radius: 20px 20px 0 0;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 48px rgba(0,0,0,0.12);
  }
`;

const CardIcon = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, rgba(255,140,0,0.12), rgba(139,69,19,0.12));
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 22px;
`;

const CardLabel = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #FF8C00, #8B4513);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: 30px;
  margin-bottom: 16px;
`;

const CardTitle = styled.h2`
  color: #1a1a1a;
  font-size: clamp(1.2rem, 1.8vw, 1.5rem);
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.3;
`;

const CardDivider = styled.div`
  width: 40px;
  height: 3px;
  background: linear-gradient(135deg, #FF8C00, #8B4513);
  border-radius: 2px;
  margin-bottom: 20px;
`;

const CardBody = styled.p`
  color: #555;
  font-size: 1rem;
  line-height: 1.85;
  margin-bottom: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: #444;
    font-size: 0.97rem;
    line-height: 1.65;
    margin-bottom: 12px;
    &::before {
      content: "✦";
      color: #FF8C00;
      font-size: 0.65rem;
      margin-top: 6px;
      flex-shrink: 0;
    }
    strong { color: #1a1a1a; }
  }
`;

// ── CTA Strip ────────────────────────────────────────────
const CTAStrip = styled.div`
  background: linear-gradient(135deg, #800000 0%, #a0522d 100%);
  border-radius: 24px;
  padding: 70px 60px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(128,0,0,0.2);
  margin-bottom: 80px;
  h3 {
    color: #fff;
    font-size: clamp(1.6rem, 3vw, 2.4rem);
    font-weight: 700;
    margin-bottom: 16px;
  }
  p {
    color: rgba(255,255,255,0.85);
    font-size: 1.1rem;
    max-width: 560px;
    margin: 0 auto 36px;
    line-height: 1.7;
  }
`;

const BookBtn = styled.a`
  display: inline-block;
  background: #fff;
  color: #800000;
  font-size: 1.1rem;
  font-weight: 700;
  padding: 18px 52px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px rgba(0,0,0,0.2);
    color: #800000;
    text-decoration: none;
  }
`;

// ── YouTube Section ──────────────────────────────────────
const YouTubeSection = styled.section`
  background: linear-gradient(135deg, rgba(139,69,19,0.05) 0%, rgba(240,116,8,0.05) 100%);
  padding: 80px 0;
  margin: 60px 0;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  &::before {
    content: '';
    position: absolute;
    top: -50%; left: -50%;
    width: 200%; height: 200%;
    background: radial-gradient(circle, rgba(240,116,8,0.1) 0%, transparent 70%);
    animation: ${gradientShift} 20s linear infinite;
  }
`;

const YouTubeTitle = styled.h1`
  color: rgba(240,116,8,0.9);
  font-size: clamp(2rem, 5vw, 3.5rem);
  text-align: center;
  margin-bottom: 50px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  z-index: 2;
`;

const YouTubeContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0,0,0,0.2);
  transition: transform 0.3s ease;
  &:hover { transform: translateY(-10px); }
  iframe {
    width: 100%;
    height: 500px;
    border: none;
    @media (max-width: 768px) { height: 300px; }
  }
`;

// ── Wine Tour Section ────────────────────────────────────
const WineSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  padding: 100px 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const WineImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  transition: transform 0.3s ease;
  &:hover { transform: translateY(-5px); }
  img {
    width: 100%;
    height: 350px;
    object-fit: cover;
    transition: transform 0.5s ease;
    &:hover { transform: scale(1.05); }
  }
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(45deg, transparent 60%, rgba(240,116,8,0.1) 100%);
    pointer-events: none;
  }
`;

const WineTextContainer = styled.div`
  width: 100%;
  h4 {
    font-size: clamp(1.2rem, 2.5vw, 1.5rem);
    line-height: 1.7;
    color: #2c3e50;
    font-weight: 400;
    padding: 30px;
    background: rgba(255,255,255,0.8);
    border-radius: 15px;
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    border-left: 5px solid rgba(240,116,8,0.8);
    margin: 0;
  }
`;

const WineTourTitle = styled.h2`
  color: #2c3e50;
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 120px;
  font-weight: 700;
  text-shadow: 0 4px 8px rgba(0,0,0,0.3);
  position: relative;
  z-index: 2;
`;

// ── Epic Cards ───────────────────────────────────────────
const CardsSection = styled.section`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 25%, #2c3e50 50%, #1a252f 100%);
  background-size: 400% 400%;
  animation: ${gradientShift} 20s ease infinite;
  padding: 100px 0;
  margin: 60px 0;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(240,116,8,0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(139,69,19,0.1) 0%, transparent 50%);
  }
`;

const CardsTitle = styled.h2`
  color: #fff;
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 120px;
  font-weight: 700;
  text-shadow: 0 4px 8px rgba(0,0,0,0.3);
  position: relative;
  z-index: 2;
`;

const CardsGrid = styled.div`
  position: relative;
  z-index: 2;
`;

const StyledRow = styled(Row)`
  justify-content: center;
  align-items: stretch;
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 100%;
`;

const StyledCol = styled(Col)`
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  padding-left: 15px;
  padding-right: 15px;
`;

const EpicCard = styled.article`
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  max-width: 600px;
  transition: all 0.4s ease;
  border: 1px solid rgba(240,116,8,0.2);
  position: relative;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
    border-color: rgba(240,116,8,0.5);
  }
`;

const CardImageContainer = styled.div`
  height: 300px;
  position: relative;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }
  ${EpicCard}:hover & img { transform: scale(1.05); }
`;

const CardGlowOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(45deg, rgba(240,116,8,0.1) 0%, transparent 50%, rgba(139,69,19,0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  ${EpicCard}:hover & { opacity: 1; }
`;

const CardContent = styled.div`
  padding: 45px;
  text-align: center;
  background: white;
  h2 {
    color: #2c3e50;
    font-size: 1.6rem;
    margin-bottom: 30px;
    font-weight: 600;
  }
`;

const CardButton = styled(Link)`
  background: linear-gradient(135deg, #FF8C00 0%, #8B4513 100%);
  color: white;
  border: none;
  padding: 18px 45px;
  border-radius: 30px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(255,140,0,0.3);
  text-decoration: none;
  display: inline-block;
  text-align: center;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255,140,0,0.4);
    color: white;
    text-decoration: none;
  }
  &:focus {
    color: white;
    text-decoration: none;
  }
`;

const WineTourLink = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 20px;
  text-decoration: none;
  padding: 30px 56px;
  border: 1px solid rgba(201, 147, 58, 0.35);
  background: linear-gradient(135deg, #1c1008, #120b05);
  color: #f5ede0;
  font-family: 'Georgia', serif;
  font-size: 1.35rem;
  letter-spacing: 0.02em;
  transition: border-color 0.4s, box-shadow 0.4s, transform 0.3s, color 0.3s;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #c9933a, transparent);
    opacity: 0;
    transition: opacity 0.4s;
  }

  &:hover {
    border-color: rgba(201, 147, 58, 0.65);
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.45);
    transform: translateY(-2px);
    color: #c9933a;
    &::before { opacity: 1; }
  }

  &:hover .wine-arrow {
    transform: translateX(5px);
  }
`

const WineArrow = styled.span`
  display: inline-block;
  color: #c9933a;
  font-size: 1.5rem;
  transition: transform 0.35s ease;
`

const WineTourEyebrow = styled.p`
  font-family: 'Trebuchet MS', sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #c9933a;
  text-align: center;
  margin-bottom: 14px;
`

// ── Component ────────────────────────────────────────────
const About = () => {
  return (
    <PageWrapper>

      {/* Video */}
      <VideoWrapper>
        <Video />
      </VideoWrapper>

      {/* Maroon Header */}
      <MaroonHeader>Little Heaven Bed & Breakfast</MaroonHeader>

      {/* Intro */}
      <IntroSection>
        <IntroInner>
          <IntroAccent />
          <IntroText>
            Discover peace like never before at Little Heaven Bed & Breakfast—a
            magical, enchanting escape where rustic charm meets modern luxury.
          </IntroText>
          <IntroText>
            Since opening our doors in September 2023, Little Heaven has stood
            as a beacon of grace and hospitality. Nestled on over{" "}
            <strong>10 acres of pristine wildlife</strong>, our estate invites
            you to slow down, breathe deeply, and reconnect with nature. The
            "rawness" of the North Carolina landscape is sure to transform your
            soul.
          </IntroText>
          <IntroBookBtn
            href="https://secure.thinkreservations.com/littleheavenbedandbreakfast/reservations"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book Now!
          </IntroBookBtn>
        </IntroInner>
      </IntroSection>

      {/* Amenities Content */}
      <AmenitiesSection>
        <AmenitiesInner>

          {/* Hero Banner */}
          <HeroBanner>
            <img
              src="https://i.postimg.cc/Pfgw0bPZ/IMG-7117.jpg"
              alt="Little Heaven Bed & Breakfast"
            />
            <HeroOverlay>
              <HeroTitle>
              A slice of heaven...
              </HeroTitle>
            </HeroOverlay>
          </HeroBanner>

          {/* Three Content Cards — no images */}
          <ContentGrid>

            {/* Card 1 — Lodging */}
            <ContentCard>
              <CardIcon>🏡</CardIcon>
              <CardLabel>Your Stay</CardLabel>
              <CardTitle>Elegant Lodging & Heavenly Amenities</CardTitle>
              <CardDivider />
              <CardBody>
                We believe the best getaways are found in the details. Our rooms
                offer a sophisticated "rustic-chic" aesthetic, featuring:
              </CardBody>
              <FeatureList>
                <li>
                  <span>
                    <strong>Brand New Accommodations:</strong> Fully renovated
                    bathrooms and flooring.
                  </span>
                </li>
                <li>
                  <span>
                    <strong>Modern Luxuries:</strong> All the high-end amenities
                    you need for a stress-free stay.
                  </span>
                </li>
                <li>
                  <span>
                    <strong>Breathtaking Views:</strong> Stunning views of the
                    surrounding wilderness right from your window.
                  </span>
                </li>
              </FeatureList>
            </ContentCard>

            {/* Card 2 — Cafe */}
            <ContentCard>
              <CardIcon>☕</CardIcon>
              <CardLabel>On-Site Dining</CardLabel>
              <CardTitle>A Culinary Journey at Little Heaven Cafe</CardTitle>
              <CardDivider />
              <CardBody>
                A stay with us is a feast for the senses. We are proud to serve
                our guests exquisite, <strong>home-made Mexican-American
                dishes</strong> and <strong>handcrafted coffee</strong> from our
                very own onsite cafe. Our mission is to provide a service like
                no other, ensuring that every meal is as memorable as the
                landscape.
              </CardBody>
            </ContentCard>

            {/* Card 3 — Location */}
            <ContentCard>
              <CardIcon>📍</CardIcon>
              <CardLabel>Location</CardLabel>
              <CardTitle>Perfectly Positioned for Adventure</CardTitle>
              <CardDivider />
              <CardBody>
                Located in the heart of the region, Little Heaven offers the
                perfect home base for your North Carolina explorations:
              </CardBody>
              <FeatureList>
                <li>
                  <span>
                    <strong>Wineries:</strong> Minutes away from the
                    award-winning tasting rooms of the Yadkin Valley.
                  </span>
                </li>
                <li>
                  <span>
                    <strong>Blue Ridge Parkway:</strong> Iconic scenic drives
                    are within easy reach.
                  </span>
                </li>
                <li>
                  <span>
                    <strong>Convenient Access:</strong> Less than an hour from
                    Winston-Salem, Greensboro, and High Point, and just over an
                    hour from Charlotte.
                  </span>
                </li>
              </FeatureList>
            </ContentCard>

          </ContentGrid>

          {/* CTA Strip */}
          <CTAStrip>
            <h3>We'd Love to Host You</h3>
            <p>
              We thank you deeply for considering us for your next retreat. We
              invite you to book your stay today and experience the magic for
              yourself.
            </p>
            <BookBtn
              href="https://secure.thinkreservations.com/littleheavenbedandbreakfast/reservations"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book Now!
            </BookBtn>
          </CTAStrip>

        </AmenitiesInner>
      </AmenitiesSection>

      {/* YouTube Video Section */}
      <ContentContainer>
        <YouTubeSection>
          <YouTubeTitle>Experience Little Heaven Bed & Breakfast:</YouTubeTitle>
          <YouTubeContainer>
            <iframe
              className="video-js"
              src="https://www.youtube.com/embed/YrphcwOyxUM?si=ez5TNkXR6XwzB4BD"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </YouTubeContainer>
        </YouTubeSection>
      </ContentContainer>

<ContentContainer>
  <div style={{ textAlign: 'center' }}>
    <WineTourEyebrow>Exclusive Service</WineTourEyebrow>
    <WineTourLink href="/localwineries">
      Wine Tour Pick-Up &amp; Drop-Off Service
      <WineArrow className="wine-arrow">→</WineArrow>
    </WineTourLink>
  </div>
</ContentContainer>

      {/* Epic Cards Section */}
      <CardsSection>
        <WideContainer>
          <CardsTitle>Discover Little Heaven</CardsTitle>
          <br /><br />
          <CardsGrid>
            <StyledRow>
              <StyledCol md={6} lg={6}>
                <EpicCard>
                  <CardImageContainer>
                    <img
                      src="https://i.postimg.cc/d08FtyFh/IMG-6230-jpg.jpg"
                      alt="Tours & Amenities"
                    />
                    <CardGlowOverlay />
                  </CardImageContainer>
                  <CardContent>
                    <h2>Tours & Amenities</h2>
                    <CardButton to="/tour">Explore Now</CardButton>
                  </CardContent>
                </EpicCard>
              </StyledCol>

              <StyledCol md={6} lg={6}>
                <EpicCard>
                  <CardImageContainer>
                    <img
                      src="https://i.postimg.cc/7Z8QPQZN/IMG-7115.jpg"
                      alt="Breakfast & Treats"
                    />
                    <CardGlowOverlay />
                  </CardImageContainer>
                  <CardContent>
                    <h2>Breakfast & Treats</h2>
                    <CardButton to="/breakfast">Learn More</CardButton>
                  </CardContent>
                </EpicCard>
              </StyledCol>

              <StyledCol md={6} lg={6}>
                <EpicCard>
                  <CardImageContainer>
                    <img src={Directions} alt="Directions" />
                    <CardGlowOverlay />
                  </CardImageContainer>
                  <CardContent>
                    <h2>Directions</h2>
                    <CardButton to="/directions">Get Directions</CardButton>
                  </CardContent>
                </EpicCard>
              </StyledCol>

              <StyledCol md={6} lg={6}>
                <EpicCard>
                  <CardImageContainer>
                    <img
                      src="https://i.postimg.cc/nMfsbw5Q/little-heaven-bed-and-breakfast.jpg"
                      alt="Policies"
                    />
                    <CardGlowOverlay />
                  </CardImageContainer>
                  <CardContent>
                    <h2>Policies</h2>
                    <CardButton to="/policies">View Info</CardButton>
                  </CardContent>
                </EpicCard>
              </StyledCol>
            </StyledRow>
          </CardsGrid>
        </WideContainer>
      </CardsSection>

    </PageWrapper>
  );
};

export default About;